import type React from 'react';
import { createContext, type ReactNode, useContext } from 'react';
import { authClient } from '../lib/authClient';

type AuthContextType = {
  isLoggedIn: boolean;
  login: (email: string, password: string, onFailure: () => void) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { data: session } = authClient.useSession();

  const login = async (email: string, password: string, onFailure: () => void) => {
    const { error } = await authClient.signIn.email({ email, password });
    if (error) {
      onFailure();
    }
  };

  const logout = async () => {
    await authClient.signOut();
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn: !!session, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
